import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

export default function QuestionRenderer({ question, answer, onAnswer, onNext, onBack }) {
  const isMultiple = question.type === 'multiple';
  const currentAnswers = Array.isArray(answer) ? answer : (answer ? [answer] : []);

  const handleSelect = (optionId, exclusive) => {
    if (!isMultiple) {
      onAnswer(optionId);
      setTimeout(onNext, 400); // Auto-advance for single choice
      return;
    }

    if (exclusive) {
      onAnswer([optionId]);
      return;
    }

    let newAnswers = [...currentAnswers];
    // Remove any exclusive answers if a normal one is clicked
    const exclusiveIds = question.options.filter(o => o.exclusive).map(o => o.id);
    newAnswers = newAnswers.filter(id => !exclusiveIds.includes(id));

    if (newAnswers.includes(optionId)) {
      newAnswers = newAnswers.filter(id => id !== optionId);
    } else {
      newAnswers.push(optionId);
    }
    onAnswer(newAnswers);
  };

  const isValid = isMultiple ? currentAnswers.length > 0 : !!answer;

  return (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-10">
      <h2 className="text-2xl md:text-3xl font-bold text-blue-900 mb-2">{question.title}</h2>
      {question.subtitle && <p className="text-gray-500 mb-6">{question.subtitle}</p>}

      <div className="space-y-3 mt-6">
        {question.options.map((option) => {
          const isSelected = currentAnswers.includes(option.id);
          return (
            <button
              key={option.id}
              onClick={() => handleSelect(option.id, option.exclusive)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-between
                ${isSelected 
                  ? 'border-blue-800 bg-blue-50 text-blue-900' 
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50 text-gray-700'}`}
            >
              <span className="font-medium text-lg">{option.label}</span>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center
                ${isMultiple ? 'rounded-md' : 'rounded-full'}
                ${isSelected ? 'border-blue-800 bg-blue-800' : 'border-gray-300'}`}>
                {isSelected && <SafeIcon name="Check" className="text-white w-4 h-4" />}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-8 flex justify-between items-center pt-6 border-t border-gray-100">
        <button
          onClick={onBack}
          className="text-gray-500 hover:text-blue-900 font-medium px-4 py-2 transition-colors"
        >
          ← Back
        </button>
        {isMultiple && (
          <button
            onClick={onNext}
            disabled={!isValid}
            className={`px-8 py-3 rounded-xl font-bold transition-all
              ${isValid 
                ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg hover:shadow-xl' 
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
          >
            Continue
          </button>
        )}
      </div>
    </div>
  );
}